/**
 * PLATFORM-DESIGN-SYSTEM-CODIFICATION-V1 -- S4 SECTION CONTAINER (the
 * fourth AdoraCare signature: EXPAND-IN-PLACE;
 * owner-home/SYSTEM-EXTRACT.md S4 + conformance C-8; the related-list
 * law as signature; the lead studio's RelatedSection candidate
 * consolidated HERE -- one disclosure primitive, not a second one).
 *
 * CONSOLIDATION: this is a THIN SIGNATURE WRAPPER over the EXISTING
 * SectionBand disclosure mode (native <details>/<summary>; one CSS
 * family, one implementation). It exists to make the S4 contract
 * REQUIRED where SectionBand leaves it optional:
 *  - counted honest header: label + COUNT (required) + census-traced
 *    stat + scope + body-governing tag, all at header level -- a
 *    collapsed header is never less honest than its body (C-8);
 *  - COUNT RULE: the count counts the body's enumerable evidence items;
 *    supplementary strips (ribbon, foot, caption) ride the header STAT,
 *    never the count (critic F4);
 *  - expansion is a REVEAL, never a navigation; navigation doors stay
 *    separate visible affordances answering the four door questions
 *    (why visible / options / click result / back out);
 *  - `?expand=` deep-linkable: pass `defaultOpen` from
 *    sectionExpandedFromSearch(location.search, expandKey) -- the pure
 *    helper below owns the URL grammar (comma-separated keys).
 *
 * FRAME LAW (page-level, proven per surface): the whole map of headers
 * lands in ONE frame in every state.
 */

import type { ReactNode } from 'react';
import SectionBand from './SectionBand';

/** The ?expand= URL grammar (C-8) lives in the pure module
 *  ui/section-expand.ts (node-suite importable, no JSX chain);
 *  re-exported here so component consumers keep one import home. */
export {
  SECTION_EXPAND_QUERY_KEY,
  sectionExpandedFromSearch,
} from './section-expand';

export interface SectionContainerProps {
  readonly title: string;
  /** REQUIRED honest count of the body's enumerable evidence items. */
  readonly count: number;
  /** Census-traced headline stat on the collapsed header. */
  readonly stat?: ReactNode;
  /** Scope printed at header level. */
  readonly scope?: string;
  /** Body-governing tag (DESIGN-ONLY / cohort window / ...) at header
   *  level. */
  readonly tag?: ReactNode;
  /** The section's ?expand= key; callers derive defaultOpen via
   *  sectionExpandedFromSearch. */
  readonly expandKey?: string;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
  readonly testId?: string;
}

export default function SectionContainer({
  title,
  count,
  stat,
  scope,
  tag,
  defaultOpen = false,
  children,
  testId,
}: SectionContainerProps): JSX.Element {
  return (
    <SectionBand
      title={title}
      count={count}
      stat={stat}
      scope={scope}
      tag={tag}
      collapsible
      defaultOpen={defaultOpen}
      testId={testId}
    >
      {children}
    </SectionBand>
  );
}
